import React, { FC, ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';

import MaterialIcon from '../../shared/common/MaterialIcon';
import { THEME_PRIMARY, themeColor } from '../../styles/basics/colors';
import { concreteLoanFormatKeyToObject } from '../../store/loanFormat/actions';
import { MATERIAL_ICON_CAMPAIGNS } from '../../shared/common/Button/Icon';
import { ProductCopyStockDropdown } from '../../shared/product/ProductCopyStock';
import { PreOrderStockDetails } from '../../store/api';
import { StockDetails } from '../../store/api';
import { useTranslation } from 'react-i18next';
import PreOrderStockDetailsList from '../../shared/product/PreOrderStockDetailsList';

const StMaterialIcon = styled(MaterialIcon)`
    font-size: 12px;
    line-height: unset;
    vertical-align: top;
`;

const StCampaignStocksItem = styled.li.attrs({ title: 'Campaign stocks' })`
    color: ${themeColor(THEME_PRIMARY)};
`;

const StCopiesInCart = styled.li.attrs({ title: 'Copies in cart' })`
    float: right !important;
    color: ${themeColor(THEME_PRIMARY)};
    margin-right: 0 !important;
`;

type Props = {
    readonly copyStockAmount?: number;
    readonly campaignStockAmount?: number;
    readonly stockDetails?: StockDetails;
    readonly preOrderStockDetails?: PreOrderStockDetails;
    readonly loanFormatKey: string;
    readonly showLabels?: boolean;
    readonly copiesInCart?: number;
};

export const CampaignProductStatistics: FC<Props> = props => {
    const { t } = useTranslation();
    const {
        copyStockAmount = 0,
        stockDetails,
        preOrderStockDetails,
        campaignStockAmount,
        showLabels,
        loanFormatKey,
        copiesInCart
    } = props;

    const loanFormat = concreteLoanFormatKeyToObject(loanFormatKey);

    const [showCopyStockSplit, setShowCopyStockSplit] = useState(false);
    const copyStockSplitPopoverRef = useRef<HTMLDivElement>(null);

    let campaignStocksIcon: ReactNode = null;
    let campaignAmount = 0;
    if (campaignStockAmount && campaignStockAmount > 0) {
        campaignStocksIcon = <StMaterialIcon icon={MATERIAL_ICON_CAMPAIGNS} />;
        campaignAmount = campaignStockAmount ?? 0;
    }

    const copiesInCartComponent =
        (copiesInCart ?? 0) > 0 ? (
            <StCopiesInCart>
                <StMaterialIcon icon="shopping_cart" />
                {copiesInCart}
            </StCopiesInCart>
        ) : null;

    return (
        <ul className="prd-dtl">
            <li>
                {(copyStockAmount ?? 0) <= 0 || !stockDetails ? (
                    <div title={t('components.productStatistics.copiesInYourLibrary')}>
                        <StMaterialIcon icon="content_copy" /> <span>0</span>
                    </div>
                ) : (
                    <div
                        data-testid="copy-stock-split-button"
                        style={{ display: 'inline-block' }}
                        onMouseEnter={() => setShowCopyStockSplit(true)}
                        onMouseLeave={() => setShowCopyStockSplit(false)}
                    >
                        <span ref={copyStockSplitPopoverRef}>
                            <StMaterialIcon icon="content_copy" />
                        </span>{' '}
                        <span>{copyStockAmount}</span>
                        <ProductCopyStockDropdown
                            loanFormat={loanFormat!}
                            stockDetails={stockDetails}
                            placement="top-start"
                            visible={showCopyStockSplit}
                            popoverRef={copyStockSplitPopoverRef}
                        />
                    </div>
                )}
            </li>

            {campaignAmount > 0 ? (
                <StCampaignStocksItem>
                    {campaignStocksIcon}
                    {campaignAmount}
                </StCampaignStocksItem>
            ) : preOrderStockDetails ? (
                <PreOrderStockDetailsList
                    loanFormat={loanFormat!}
                    preOrderStockDetails={preOrderStockDetails}
                    showLabels={showLabels}
                    isCampaignProduct={true}
                />
            ) : null}

            {copiesInCartComponent}
        </ul>
    );
};

export default CampaignProductStatistics;
